import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import * as clientsService from '../services/clients';
import * as auth from './auth/authSlice';
import { userConfigsSelectors } from './userConfigs';
import { entitiesAdapter } from './common/adapter';

const initialState = {
  isFetching: false,
  isCreating: false,
  isUpdating: false,

  fetchingError: null,
  creatingUpdatingError: null,
};

const sliceName = 'clients';

export const fetchAll = createAsyncThunk(
  `${sliceName}/fetchAll`,
  (args, thunkAPI) => {
    return clientsService.getClients()
      .catch((error) => {
        error.clientMessage = "Can't get clients from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const fetchById = createAsyncThunk(
  `${sliceName}/fetchById`,
  (args, thunkAPI) => {
    return clientsService.getClientById(args.id, auth.logout)
      .catch((error) => {
        error.clientMessage = "Can't get client from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const create = createAsyncThunk(
  `${sliceName}/create`,
  (args, thunkAPI) => {
    return clientsService.addClient(
      args.legalForm, args.name, args.taxId, args.contactPerson,
      args.e_mail, args.phone, args.legalAddress
    )
      .catch((error) => {
        error.clientMessage = "Can't post client to server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const updateClient = createAsyncThunk(
  `${sliceName}/update`,
  (args, thunkAPI) => {
    return clientsService.updateClient(
      args.id, args.legalForm, args.name, args.taxId, args.contactPerson,
      args.e_mail, args.phone, args.legalAddress
    )
      .catch((error) => {
        error.clientMessage = "Can't update clients on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const addToFavorites = createAsyncThunk(
  `${sliceName}/favoritesAdd`,
  (args) => {
    return clientsService.addClientToFavorites(args.id)
  }
);
export const removeFromFavorites = createAsyncThunk(
  `${sliceName}/favoritesRemove`,
  (args) => {
    return clientsService.removeClientFromFavorites(args.id)
  }
);

const clients = createSlice({
  name: sliceName,
  initialState: entitiesAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: {
    [fetchAll.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.setAll(state, action);
    },
    [fetchById.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [create.fulfilled]: (state, action) => {
      state.isCreating = false;
      entitiesAdapter.addOne(state, action);
    },
    [updateClient.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [addToFavorites.fulfilled]: entitiesAdapter.upsertOne,
    [removeFromFavorites.fulfilled]: entitiesAdapter.upsertOne,

    [fetchAll.pending]: (state) => {
      state.isFetching = true;
      state.creatingUpdatingError = null;
      state.fetchingError = null;
    },
    [fetchById.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [create.pending]: (state) => {
      state.isCreating = true;
      state.fetchingError = null;
      state.creatingUpdatingError = null;
    },
    [updateClient.pending]: (state) => {
      state.isUpdating = true;
      state.fetchingError = null;
      state.creatingUpdatingError = null;
    },

    [fetchAll.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },
    [fetchById.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },
    [create.rejected]: (state, action) => {
      state.isCreating = false;
      state.creatingUpdatingError = action.payload;
    },
    [updateClient.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingError = action.payload;
    },
  }
});

const { reducer } = clients;

const clientsAdapterSelectors = entitiesAdapter.getSelectors((state) => state[sliceName]);

const selectedClientsSelector = createSelector(
  (state) => userConfigsSelectors.selectedItems(state, sliceName),
  (state) => clientsAdapterSelectors.selectEntities(state),
  (selectedIds, entities) => selectedIds.map((id) => entities[id]),
);

export const clientsSelectors = {
  ...clientsAdapterSelectors,
  isFetching: (state) => state[sliceName].isFetching,
  isCreating: (state) => state[sliceName].isCreating,
  isUpdating: (state) => state[sliceName].isUpdating,

  fetchingError: (state) => state[sliceName].fetchingError,
  creatingUpdatingError: (state) => state[sliceName].creatingUpdatingError,

  state: (state) => state[sliceName],
  selectedClients: selectedClientsSelector,
};

export default reducer;



// WEBPACK FOOTER //
// ./src/store/clientsSlice.js